/**
 * User Entity Schema & Types
 */
import type { Tables } from '@popow/infra/types/database.types';
import { z } from 'zod';

// ============ Row 타입 (DB) ============
export type UserProfileRow = Tables<'user_profiles'>;

export const userProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  username: z.string().nullable(),
  avatar_url: z.string().nullable(),
  bio: z.string().nullable(),
  tiktok_handle: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

const tiktokVideoSchema = z
  .object({
    id: z.string().optional(),
    title: z.string().nullable().optional(),
    cover_image_url: z.string().nullable().optional(),
    share_url: z.string().nullable().optional(),
    create_time: z.number().nullable().optional(),
    duration: z.number().nullable().optional(),
  })
  .passthrough();

const tiktokStatsSchema = z
  .object({
    bio_description: z.string().nullable().optional(),
    is_verified: z.boolean().nullable().optional(),
    follower_count: z.number().nullable().optional(),
    following_count: z.number().nullable().optional(),
    likes_count: z.number().nullable().optional(),
    video_count: z.number().nullable().optional(),
  })
  .passthrough();

export const userProfileWithTikTokSchema = userProfileSchema
  .extend({
    tiktok_open_id: z.string().nullable(),
    tiktok_sec_uid: z.string().nullable(),
    tiktok_videos: z.array(tiktokVideoSchema).nullable(),
    tiktok_stats: tiktokStatsSchema.nullable(),
  })
  .passthrough();

/**
 * Join 시 필요한 user 필드만 pick한 스키마
 * id → user_id로 rename하여 다른 엔티티와 충돌 방지
 */
export const joinedUserSchema = z.object({
  user_id: z.string().uuid(),
  username: z.string().nullable(),
  avatar_url: z.string().nullable(),
  tiktok_handle: z.string().nullable(),
});

// ============ Domain 타입 (스키마에서 추출) ============
export type UserProfile = z.infer<typeof userProfileSchema>;
export type TikTokVideo = z.infer<typeof tiktokVideoSchema>;
export type TikTokStats = z.infer<typeof tiktokStatsSchema>;
export type UserProfileWithTikTok = z.infer<typeof userProfileWithTikTokSchema>;
export type JoinedUser = z.infer<typeof joinedUserSchema>;

// ============ Row → Domain 파서 ============
export function parseUserProfileRow(row: UserProfileRow): UserProfile {
  return userProfileSchema.parse(row);
}

export function parseUserProfileWithTikTokRow(row: UserProfileRow): UserProfileWithTikTok {
  return userProfileWithTikTokSchema.parse(row);
}
